import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import type { AuditArea, AuditDomainRating, AuditLastRun, AuditSide, AuditType } from '../../api/audit'

/**
 * Cele zece audituri, ca zece carduri mari, cu ariile lor pe ele - macheta
 * `settings/audit/_grid.php`, aceleasi clase.
 *
 * Un audit se alege dupa ARIILE lui, nu dupa nume: tot ce e de citit e pe card,
 * fiecare arie se deschide PE LOC (ce se uita acolo, ce extrage agentul, ce se
 * decide fara om, ce ramane la auditor), iar pagina tipului ramane pentru plan
 * si rulare. Codurile (1.1, 2.4) nu se vad - stau in `data-code`.
 */

/** `Rating::colour()`: 1-2 rosu, 3 galben, 4-5 verde - transcris, ca sa nu ceara un drum la server pe card. */
function colour(rating: number): string {
  if (rating <= 2) return 'red'
  if (rating === 3) return 'amber'
  return 'green'
}

/**
 * Annex A al unei arii: ce se ia, ce se decide fara om, ce ramane la auditor.
 * `between` e ce pune pagina tipului intre „extracts" si „checks" - cele trei
 * coloane CMDB / masina / om, exact unde le are `type.php`.
 */
export function AreaSpec({
  area,
  between,
  auditor = true,
}: {
  area: AuditArea
  between?: React.ReactNode
  /* Pe cardul planului, „Stays with the auditor" sta in jumatatea manuala, cu
     intrebarile - nu aici. Catalogul il vrea in continuare. */
  auditor?: boolean
}) {
  return (
    <>
      {area.extracts !== '' && (
        <div className="od-audit-spec-part">
          <span className="od-audit-step-label">
            <i className="fas fa-download" aria-hidden="true"></i> The agent extracts
          </span>
          <p>{area.extracts}</p>
        </div>
      )}
      {between}
      {area.checks !== '' && (
        <div className="od-audit-spec-part">
          <span className="od-audit-step-label">
            <i className="fas fa-calculator" aria-hidden="true"></i> Checks that run by themselves
          </span>
          <p>{area.checks}</p>
        </div>
      )}
      {auditor && area.auditor !== '' && (
        <div className="od-audit-spec-part od-audit-spec-human">
          <span className="od-audit-step-label">
            <i className="fas fa-user-tie" aria-hidden="true"></i> Stays with the auditor
          </span>
          <p>{area.auditor}</p>
        </div>
      )}
    </>
  )
}

export function SidePills({ area, sides }: { area: AuditArea; sides: Record<string, AuditSide> }) {
  return (
    <>
      {area.sides.map((side) => (
        <span key={side} className={`od-audit-side od-audit-side-${side}`} title={sides[side]?.about ?? ''}>
          <i className={`fas ${sides[side]?.icon ?? ''}`} aria-hidden="true"></i> {sides[side]?.short ?? side}
        </span>
      ))}
    </>
  )
}

function AreaRow({ area, sides, hidden }: { area: AuditArea; sides: Record<string, AuditSide>; hidden: boolean }) {
  const [open, setOpen] = useState(false)
  const panel = 'areaOf' + area.code.replace(/\./g, '_')
  return (
    <li className="od-audit-area" data-code={area.code} hidden={hidden}>
      <button type="button" className="od-audit-area-head" aria-expanded={open} aria-controls={panel} onClick={() => setOpen(!open)}>
        <i className="fas fa-chevron-right od-audit-chevron" aria-hidden="true"></i>
        <span className="od-audit-title">{area.title}</span>
        <span className={`adm-pill od-audit-mode od-audit-mode-${area.mode.toLowerCase()}`}>{area.mode}</span>
        <span className="od-audit-auto-n">{area.auto}%</span>
      </button>
      <div className="od-audit-area-body" id={panel} hidden={!open}>
        <p className="od-audit-scope">{area.scope}</p>
        <p className="od-audit-area-sides">
          <SidePills area={area} sides={sides} />
        </p>
        <AreaSpec area={area} />
      </div>
    </li>
  )
}

/** Cautarea ingusteaza, nu aduce: o arie se potriveste pe titlu, scop, mod si Annex A. */
export function areaMatches(area: AuditArea, needle: string): boolean {
  if (needle === '') return true
  return [area.title, area.scope, area.extracts, area.checks, area.auditor, area.mode].join(' ').toLowerCase().includes(needle)
}

export function AuditGrid({
  types,
  runCounts,
  lastRuns,
  ratings,
  sides,
  needle = '',
}: {
  types: AuditType[]
  runCounts: Record<string, number>
  lastRuns: Record<string, AuditLastRun>
  ratings: Record<string, AuditDomainRating>
  sides: Record<string, AuditSide>
  needle?: string
}) {
  const q = needle.trim().toLowerCase()
  return (
    <div className="od-audit-grid">
      {types.map((type) => {
        const id = String(type.id)
        const runs = runCounts[id] ?? 0
        const last = lastRuns[id] ?? null
        const mark = ratings[id] ?? null
        const shown = type.areas.filter((a) => areaMatches(a, q)).length
        return (
          <section
            key={type.id}
            className="adm-card od-audit-box od-audit-type"
            data-type={type.id}
            id={`audit-type-${type.id}`}
            hidden={q !== '' && shown === 0}
          >
            <header className="od-audit-box-head">
              <div className="od-audit-box-title">
                <Link to="/settings/audit/type/$id" params={{ id }} className="od-audit-box-name">
                  {type.title}
                </Link>
                <p className="od-audit-box-note">{type.objective}</p>
              </div>
              {mark !== null && mark.mean !== null ? (
                <span
                  className={`od-audit-rate od-audit-rate-${colour(Math.round(mark.mean))}`}
                  title={`mean of ${mark.rated} rated area(s); the lowest is ${mark.lowest}`}
                >
                  {mark.mean.toFixed(1)}
                  <span className="od-audit-rate-low">low {mark.lowest}</span>
                </span>
              ) : (
                <span className="adm-pill od-audit-auto" title="share of this type's evidence that runs without human judgement">
                  {type.automation}% auto
                </span>
              )}
            </header>

            <div className="od-audit-box-facts">
              {mark !== null && mark.flagged.length > 0 && (
                <>
                  <span className="od-audit-flag" title="an area rated 1 is flagged whatever the mean says">
                    <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> {mark.flagged.length} at 1
                  </span>
                  <span className="od-audit-dot">&middot;</span>
                </>
              )}
              <span>{type.areas.length} areas</span>
              <span className="od-audit-dot">&middot;</span>
              <span>
                {type.mix[0]} auto, {type.mix[1]} assisted, {type.mix[2]} manual
              </span>
            </div>

            <ul className="od-audit-areas">
              {type.areas.map((area) => (
                <AreaRow key={area.code} area={area} sides={sides} hidden={!areaMatches(area, q)} />
              ))}
            </ul>

            <footer className="od-audit-box-foot">
              <span className="od-audit-box-when">
                {last === null ? (
                  <span className="adm-muted">never run for these companies</span>
                ) : last.status === 'done' ? (
                  <>
                    <i className="fas fa-check od-audit-yes" aria-hidden="true"></i> Last run {last.at}
                    {runs > 1 && <> &middot; {runs} in all</>}
                  </>
                ) : (
                  <>
                    <span className="od-audit-unfinished">{last.status}</span> since {last.at}
                  </>
                )}
              </span>
              <Link className="od-audit-box-open" to="/settings/audit/type/$id" params={{ id }}>
                open the plan <i className="fas fa-arrow-right" aria-hidden="true"></i>
              </Link>
            </footer>
          </section>
        )
      })}
    </div>
  )
}
